import React, { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import validate from 'validate.js';
import clsx from 'clsx';
import { makeStyles } from '@mui/styles';
import _ from 'underscore';
import {
	Button,
	Card,
	CardActions,
	CardContent,
	CardHeader,
	Grid,
	Divider,
	colors,
	TextField
} from '@mui/material';
import DateTimePicker from 'components/DatePicker/DateTimePicker.js';

import { SelectField } from 'components';

import { SetFormState, GetTypes, GetTeamsByType, GetStages, EditTicket } from '../../../../../actions/TicketsActions.js';

const schema = {
	field: {
		presence: { allowEmpty: false, message: 'is required' },
	}
};

const useStyles = makeStyles((theme) => ({
	root: {
		marginTop: theme.spacing(1),
	},

	generate: {
		color: theme.palette.white,
		backgroundColor: colors.green[600],
		'&:hover': {
			backgroundColor: colors.green[900],
		},
	},
}));

const DetailsForm = (props) => {
	const { className, details, ticketId, ...rest } = props;
	const classes = useStyles();
	const dispatch = useDispatch();

	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector((state) => state.ticketsReducer.formState);
	const user = useSelector((state) => state.session.user);
	const types = useSelector((state) => state.ticketsReducer.types);
	const teams = useSelector((state) => state.ticketsReducer.teams);
	const activities = useSelector((state) => state.ticketsReducer.activities);
	const stages = useSelector((state) => state.ticketsReducer.stages);

	const _usePrevious = (value) => {
		const ref = useRef();
		useEffect(() => {
			ref.current = value;
		});
		return ref.current;
	};

	const _useHasChanged = (val) => {
		const prevVal = _usePrevious(val);
		return prevVal !== val;
	};

	const hasActivityChanged = _useHasChanged(formState.values.activityId);
	const hasTypeChanged = _useHasChanged(formState.values.typeId);
	const hasStageChanged = _useHasChanged(formState.values.stageId);

	useEffect(() => {
		const errors = validate(formState.values, schema, { fullMessages: false });

		setFormState({
			...formState,
			isValid: !errors,
			errors: errors || {},
		});

		if (hasActivityChanged && _.isEqual(ticketId, formState.values.id)) {
			dispatch(GetTypes(formState.values.activityId, 1));
		}

		if (hasTypeChanged && formState.values.typeId !== '' && _.isEqual(ticketId, formState.values.id)) {
			dispatch(GetTeamsByType(formState.values.typeId, 1));
		}

		if (hasStageChanged && _.isEqual(ticketId, formState.values.id)) {
			dispatch(GetStages(formState.values.stageId, 1));
		}
	}, [formState.values]);

	const _handleChange = (event) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[event.target.name]:
					event.target.type === 'checkbox'
						? event.target.checked
						: event.target.value,
			},
			touched: {
				...formState.touched,
				[event.target.name]: true,
			},
		});
	};

	const _handleChangeActivity = (event) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				typeId: '',
				teamId: '',
				activityId: event.target.value,
			},
			touched: {
				...formState.touched,
				activityId: true,
			},
		});
	};

	const _handleDateTimeChange = (value) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				targetExecutionDate: value,
			},
			touched: {
				...formState.touched,
				targetExecutionDate: true,
			},
		});
	};

	const _hasError = (field) => (!!(formState.touched[field] && formState.errors[field]));

	const _save = () => {
		const values = { ...formState.values, userId: user.id };
		dispatch(EditTicket(values));
	};

	return (
		<Card {...rest} className={clsx(classes.root, className)}>
			<CardHeader title="Details" />
			<Divider />
			<CardContent>
				<Grid container spacing={4}>
					<Grid item md={6} xs={12}>
						<TextField
							error={_hasError('subject')}
							fullWidth
							helperText={
								_hasError('subject') ? formState.errors.subject[0] : null
							}
							label="Subject"
							name="subject"
							onChange={_handleChange}
							value={formState.values.subject || ''}
							disabled
							variant="outlined"
						/>
					</Grid>
					<Grid item md={6} xs={12}>
						<DateTimePicker
							style={{ width: '100%' }}
							label="Target Execution Date"
							value={formState.values.targetExecutionDate}
							onChange={_handleDateTimeChange}
							error={_hasError('targetExecutionDate')}
							disabled
							helperText={_hasError('targetExecutionDate') ? formState.errors.targetExecutionDate[0] : null}
						/>
					</Grid>
					<Grid item md={4} xs={12}>
						<SelectField
							id="activity"
							name="activityId"
							value={formState.values.activityId || ''}
							onChange={_handleChangeActivity}
							hintText="Activity"
							menuItems={activities}
							disabled
							error={_hasError('activityId') ? formState.errors.activityId[0] : null}
						/>
					</Grid>
					<Grid item md={4} xs={12}>
						<SelectField
							id="type"
							name="typeId"
							value={formState.values.typeId || ''}
							onChange={_handleChange}
							hintText="Type"
							menuItems={types}
							disabled
							error={_hasError('typeId') ? formState.errors.typeId[0] : null}
						/>
					</Grid>
					<Grid item md={4} xs={12}>
						<SelectField
							id="team"
							name="teamId"
							value={formState.values.teamId || ''}
							onChange={_handleChange}
							hintText="Team"
							menuItems={teams}
							disabled
							error={_hasError('teamId') ? formState.errors.teamId[0] : null}
						/>
					</Grid>
					<Grid item md={12} xs={12}>
						<SelectField
							id="stage"
							name="stageId"
							value={formState.values.stageId || ''}
							onChange={_handleChange}
							hintText="Stage"
							menuItems={stages}
							error={_hasError('stageId') ? formState.errors.stageId[0] : null}
						/>
					</Grid>
				</Grid>
			</CardContent>
			<Divider />
			<CardActions>
				<Button
					className={classes.generate}
					variant="contained"
					onClick={_save}
					disabled={details && _.isEqual(details, formState.values)}
				>
					Save
				</Button>
			</CardActions>
		</Card>
	);
};

DetailsForm.propTypes = {
	className: PropTypes.object,
	details: PropTypes.object,
	ticketId: PropTypes.number,
};

export default DetailsForm;
